/**
 * ImageGenHelper — drop-in panel that turns the teacher's natural language
 * request into a rich Nano-Banana-Flash-ready prompt, lets them edit it,
 * then renders the image. The render uses the existing studio image endpoint
 * which we just upgraded to prefer `gemini-3.1-flash-image-preview`.
 *
 * Flow:
 *   1. Teacher types what they want, OR presses "Suggest prompt", which drafts
 *      a rich prompt from the "Add To" section's content (the typed text is
 *      not used) and puts it straight into the same box
 *   2. Teacher reviews / edits the box
 *   3. Press "Create image" → image rendered from the box
 *   4. Optionally "Insert into section"
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  X, Wand2, ImagePlus, Loader2, Sparkles, RefreshCw, Plus, History, AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import { notifyError } from '../../lib/errors';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GREEN = '#34A853';
const SOFT = '#F0FDF4';

const STYLES = [
  { id: 'diagram',    label: 'Diagram',    sub: 'Labeled science diagram' },
  { id: 'whiteboard', label: 'Whiteboard', sub: 'Hand-drawn classroom feel' },
  { id: 'photo',      label: 'Photo',      sub: 'Realistic photograph' },
  { id: 'isometric',  label: 'Isometric',  sub: '3D iso illustration' },
  { id: '3d',         label: '3D Render',  sub: 'Premium 3D model' },
  { id: 'lab',        label: 'Lab',        sub: 'Lab equipment / apparatus' },
  { id: 'concept',    label: 'Concept',    sub: 'Minimalist concept art' },
];

function authHeaders() {
  const t = localStorage.getItem('desky_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
}

export default function ImageGenHelper({ open, onClose, draftId, sectionId, sections = [], subject = null, onImageReady }) {
  const [request, setRequest] = useState('');
  const [style, setStyle] = useState('diagram');
  // Which section the generated image gets added to. Defaults to the section the
  // panel was opened from, but the "Add To" dropdown lets the teacher retarget it.
  const [targetSectionId, setTargetSectionId] = useState(sectionId || '');
  const [labels, setLabels] = useState([]);
  const [renderModel, setRenderModel] = useState('gemini-3.1-flash-image-preview');
  const [suggesting, setSuggesting] = useState(false);
  const [creating, setCreating] = useState(false);
  const [image, setImage] = useState(null);
  const [usedModel, setUsedModel] = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    if (!open) {
      setImage(null);
      setUsedModel(null);
    }
  }, [open]);

  // Keep the "Add To" target in sync with the section the panel was opened from.
  // Fall back to the first available section so Create always has a valid target.
  useEffect(() => {
    if (!open) return;
    setTargetSectionId(sectionId || sections[0]?.id || '');
  }, [open, sectionId, sections]);

  const targetSection = sections.find(s => s.id === targetSectionId);

  // A different target section makes the previous suggestion's labels stale.
  const changeTarget = (id) => { setTargetSectionId(id); setLabels([]); };

  // Drafts a prompt from the selected section — what's typed in the box is
  // deliberately not sent, and the draft replaces it so the teacher reviews
  // and edits it in place.
  const suggest = async () => {
    if (!targetSection) { toast.error('Pick a section to suggest a prompt for'); return; }
    setSuggesting(true);
    try {
      const res = await axios.post(`${API}/teacher/studio/image/prompt-suggest`,
        { request: '', style, subject, section_context: targetSection.context || `Section: ${targetSection.title || ''}` },
        { headers: authHeaders() });
      if (!res.data.prompt) { toast.error("Couldn't suggest a prompt for this section. Please try again."); return; }
      setRequest(res.data.prompt);
      setLabels(res.data.labels || []);
      setRenderModel(res.data.render_model || 'gemini-3.1-flash-image-preview');
      toast.success('Prompt suggested — review it and create the image');
    } catch (e) {
      notifyError(e, "Couldn't suggest a prompt. Please try again.");
    } finally { setSuggesting(false); }
  };

  const create = async () => {
    // Whatever is in the box — typed by the teacher or filled by Suggest prompt.
    const effectivePrompt = request.trim();
    if (!effectivePrompt) { toast.error('Type what you want first'); return; }
    if (!draftId || !targetSectionId) {
      toast.error('Pick a section to add the image to');
      return;
    }
    setCreating(true);
    try {
      const res = await axios.post(`${API}/teacher/studio/generate-image`,
        { draft_id: draftId, section_id: targetSectionId, prompt: effectivePrompt },
        { headers: authHeaders() });
      setImage(res.data.image);
      setUsedModel(res.data.model || renderModel);
      setHistory(prev => [{ image: res.data.image, prompt: effectivePrompt, model: res.data.model, ts: Date.now() }, ...prev].slice(0, 5));
      toast.success(`Image rendered with ${res.data.model || renderModel}`);
    } catch (e) {
      notifyError(e, "Couldn't generate the image. Please try again.");
    } finally { setCreating(false); }
  };

  const insert = () => {
    if (!image) return;
    onImageReady?.({ image, prompt: request.trim(), model: usedModel, sectionId: targetSectionId });
    toast.success('Image inserted into section');
    onClose?.();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[65] flex" style={{ background: 'rgba(8,16,12,0.72)' }} data-testid="image-gen-helper">
      <div className="ml-auto h-full w-full sm:w-[640px] lg:w-[760px] bg-white shadow-2xl flex flex-col">
        <div className="px-5 py-4 border-b border-slate-100 flex items-start justify-between gap-3">
          <div>
            <div className="text-xs font-semibold text-green-700 inline-flex items-center gap-1.5">
              <ImagePlus className="w-3 h-3"/> Image Studio
            </div>
            <h2 className="text-lg font-semibold text-slate-900 mt-0.5" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Auto-prompt &amp; create
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Type what you want, or let Suggest prompt draft one from the section — Nano Banana Flash renders it.
            </p>
          </div>
          <button onClick={onClose} data-testid="img-close" className="text-slate-400 hover:text-slate-700 p-1 -m-1 rounded-md hover:bg-slate-100">
            <X className="w-5 h-5"/>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {/* Request */}
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <label className="text-xs font-semibold text-slate-500 ">What image do you want?</label>
            <textarea value={request} onChange={e => setRequest(e.target.value)} rows={request.length > 120 ? 5 : 2}
              data-testid="img-request"
              placeholder="e.g. diagram of plant mitosis · labeled chemistry beaker setup · forces on inclined plane · DNA structure"
              className="mt-2 w-full text-sm rounded-lg border border-slate-200 px-3 py-2 focus:ring-2 focus:ring-green-200 focus:outline-none"/>
            {labels.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5" data-testid="img-labels">
                {labels.map((l, i) => (
                  <span key={i} className="text-xs font-semibold px-2 py-0.5 rounded-full bg-green-50 border border-green-200 text-green-700">{l}</span>
                ))}
              </div>
            )}

            <div className="mt-2 flex items-center gap-1.5 flex-wrap">
              <span className="text-xs font-semibold text-slate-500 ">Style</span>
              {STYLES.map(s => (
                <button key={s.id} onClick={() => setStyle(s.id)}
                  data-testid={`img-style-${s.id}`}
                  className={`text-xs font-semibold px-2 py-0.5 rounded-full border transition-colors ${
                    style === s.id ? 'bg-green-600 text-white border-green-600' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
                  }`} title={s.sub}>{s.label}</button>
              ))}
            </div>

            {/* Add To — pick which section the generated image is added to */}
            {sections.length > 0 && (
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <span className="text-xs font-semibold text-slate-500 ">Add To</span>
                <select value={targetSectionId} onChange={e => changeTarget(e.target.value)}
                  data-testid="img-add-to-section"
                  className="text-xs rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-slate-700 focus:ring-2 focus:ring-green-200 focus:border-green-300 focus:outline-none transition max-w-[320px]">
                  {sections.map((s, i) => (
                    <option key={s.id} value={s.id}>{`${i + 1}. ${s.title || 'Untitled section'}`}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="mt-3 flex gap-2 flex-wrap">
              <button onClick={suggest} disabled={suggesting || !targetSection} data-testid="img-suggest-btn"
                title={targetSection ? `Draft a prompt from "${targetSection.title || 'this section'}"` : 'Pick a section first'}
                className="h-10 px-4 text-sm font-semibold text-white rounded-xl inline-flex items-center gap-2 disabled:opacity-50 disabled:shadow-none transition active:scale-[0.98]"
                style={{ background: `linear-gradient(135deg, ${GREEN}, #0d9488)` }}>
                {suggesting ? <Loader2 className="w-4 h-4 animate-spin"/> : <Wand2 className="w-4 h-4"/>}
                Suggest prompt
              </button>
              {/* Always-visible Create button — renders directly from the typed
                  request; Suggest prompt is optional polish, not a gate. */}
              <button onClick={create} disabled={creating || !request.trim()} data-testid="img-create-btn"
                className="h-10 px-4 text-sm font-semibold text-white rounded-xl inline-flex items-center gap-2 disabled:opacity-40 disabled:shadow-none transition active:scale-[0.98]"
                style={{ background: request.trim() ? 'linear-gradient(135deg, #7c3aed, #4f46e5)' : '#94a3b8' }}
                title={!request.trim() ? 'Type what you want, or press Suggest prompt' : 'Render with Nano Banana Flash'}>
                {creating ? <Loader2 className="w-4 h-4 animate-spin"/> : <ImagePlus className="w-4 h-4"/>}
                Create image
              </button>
            </div>
          </div>

          {/* Generated image */}
          {image && (
            <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="img-result-card">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-slate-500">Result</span>
                <span className="text-xs text-green-700 font-semibold inline-flex items-center gap-1"><Sparkles className="w-3 h-3"/> {usedModel}</span>
              </div>
              <div className="bg-slate-50 rounded-lg p-2 flex items-center justify-center" style={{ minHeight: 220 }}>
                <img alt="" src={image} className="max-w-full max-h-[360px] object-contain rounded"/>
              </div>
              <div className="mt-3 flex gap-2">
                <button onClick={create} disabled={creating} data-testid="img-regen-btn"
                  className="h-9 px-3 text-xs font-semibold text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 inline-flex items-center gap-1.5">
                  <RefreshCw className="w-3.5 h-3.5"/> Regenerate
                </button>
                {onImageReady && (
                  <button onClick={insert} data-testid="img-insert-btn"
                    className="ml-auto h-9 px-4 text-xs font-semibold text-white rounded-lg inline-flex items-center gap-1.5 shadow"
                    style={{ background: GREEN }}>
                    <Plus className="w-3.5 h-3.5"/> Insert into section
                  </button>
                )}
              </div>
            </div>
          )}

          {/* History */}
          {history.length > 1 && (
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="text-xs font-semibold text-slate-500 mb-2 inline-flex items-center gap-1"><History className="w-3 h-3"/> Recent renders</div>
              <div className="grid grid-cols-5 gap-2">
                {history.slice(1).map((h, i) => (
                  <button key={i} onClick={() => { setImage(h.image); setUsedModel(h.model); }}
                    className="aspect-square rounded-md overflow-hidden border border-slate-200 hover:border-green-300 transition-colors">
                    <img alt="" src={h.image} className="w-full h-full object-cover"/>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Fallback hint */}
          {!image && !request.trim() && (
            <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg p-3 inline-flex items-start gap-2">
              <AlertTriangle className="w-3.5 h-3.5 text-amber-500 mt-0.5 flex-shrink-0"/>
              <span>Type your idea above and press <strong>Create image</strong>, or press <strong>Suggest prompt</strong> to draft a prompt from the selected section — it appears in the box for you to review and edit.</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
